// sidebar/Sidebar.js
import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

import { NavLink } from "react-router-dom";
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Tooltip,
    styled,
    useTheme,
    alpha,
} from "@mui/material";

// MUI Icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import DevicesIcon from "@mui/icons-material/Devices";
import SendIcon from "@mui/icons-material/Send";
import AppsIcon from "@mui/icons-material/Apps";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import PeopleIcon from "@mui/icons-material/People";
import SummarizeIcon from "@mui/icons-material/Summarize";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AppBlockingIcon from '@mui/icons-material/AppBlocking';
import NumbersIcon from '@mui/icons-material/Numbers';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import GroupAddIcon from '@mui/icons-material/GroupAdd';

import axios from "axios";
import DeviceService from "../../../services/devices/DeviceService";
import { setDeviceConnectionMessage } from "../../../redux/slices/checkDeviceIsConnectionSlice";
import Badge from '@mui/material/Badge';
import { Chat } from "@mui/icons-material";

const drawerWidth = 260;

const modernOpenedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    background: `linear-gradient(180deg, ${theme.palette.mode === 'dark' ? '#202020' : '#1A237E'} 0%, ${theme.palette.mode === 'dark' ? '#121212' : '#0F172A'} 100%)`,
    borderRight: "none",
    color: theme.palette.common.white,
});

const modernClosedMixin = (theme) => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
    background: `linear-gradient(180deg, ${theme.palette.mode === 'dark' ? '#202020' : '#1A237E'} 0%, ${theme.palette.mode === 'dark' ? '#121212' : '#0F172A'} 100%)`,
    borderRight: "none",
    color: theme.palette.common.white,
});

const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== "open" })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        ...(open && {
            ...modernOpenedMixin(theme),
            "& .MuiDrawer-paper": {
                ...modernOpenedMixin(theme),
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { backgroundColor: alpha(theme.palette.common.black, 0.1) },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: alpha(theme.palette.common.white, 0.2),
                    borderRadius: '3px',
                    '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.3) },
                },
            }
        }),
        ...(!open && {
            ...modernClosedMixin(theme),
            "& .MuiDrawer-paper": {
                ...modernClosedMixin(theme),
                '&::-webkit-scrollbar': { display: 'none' },
            }
        }),
    })
);

// DrawerHeader will align left with margin when open, and center when closed.
const DrawerHeader = styled("div", { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: open ? "flex-start" : "center",
    padding: theme.spacing(0, 1),
    minHeight: 64,
    ...(open ? { marginLeft: 25 } : {}),
}));


const Sidebar = ({ open, onListItemClick }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const profileDataState = useSelector((state) => state.profile.profile);
    const [companylogo, setCompanyLogo] = useState(null);
    const [deviceList, setDeviceList] = useState([]);
    const [allowMyAppsShow, setAllowMyAppsShow] = useState('no');
    const [activePlan, setActivePlan] = useState('Starter Plan');
    const [allowedFeatures, setAllowedFeatures] = useState({});
    const checkDeviceDataState = useSelector(
        (state) => state.checkDeviceIsConnectionState.message
    );

    const [userNotifCount, setUserNotifCount] = useState(0);
    const [adminNotifCount, setAdminNotifCount] = useState(0);
    const [adminRoleAccess, setAdminRoleAccess] = useState();

    const token = localStorage.getItem("isToken");
    useEffect(() => {
        const fetchUserNotifCount = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_NODE_URL}/api/help_support/get-user-notification-count`, {
                    headers: { token },
                });
                if (res.data.isSuccess) {
                    setUserNotifCount(res.data.count);
                }
            } catch (error) {
                console.error("Error fetching user notification count:", error);
            }
        };

        fetchUserNotifCount();
        const interval = setInterval(fetchUserNotifCount, 30000);
        return () => clearInterval(interval);
    }, [token]);

    useEffect(() => {
        const fetchAdminNotifCount = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_NODE_URL}/api/help_support/get-admin-notification-count`, {
                    headers: { token },
                });
                if (res.data.isSuccess) {
                    setAdminNotifCount(res.data.count);
                }
            } catch (error) {
                console.error("Error fetching admin notification count:", error);
            }
        };

        fetchAdminNotifCount();
        const interval = setInterval(fetchAdminNotifCount, 30000);
        return () => clearInterval(interval);
    }, [token]);

    const checkTabGrants = useCallback(() => {
        const roleArray = localStorage.getItem("roleArray");
        if (roleArray) {
            const checkList = roleArray.split(",");
            if (checkList.includes("admindashboard")) {
                setAdminRoleAccess("AdminAccess");
            } else {
                setAdminRoleAccess("UserAccess");
            }
        }
    }, []);

    useEffect(() => {
        checkTabGrants();
    }, [checkTabGrants]);

    // Load allowed features from localStorage (stored as JSON string)
    useEffect(() => {
        const raw = localStorage.getItem('allowedFeatures');
        if (!raw) return;
        try {
            const parsed = JSON.parse(raw);
            setAllowedFeatures(parsed || {});
        } catch (err) {
            console.error('Failed to parse allowedFeatures from localStorage', err);
            setAllowedFeatures({});
        }
    }, []);

    const helpNotifCount = adminRoleAccess === "AdminAccess" ? adminNotifCount : userNotifCount;

    const [expandedAccordion, setExpandedAccordion] = useState(false);

    const checkTabGrant = useCallback((pathArr) => {
        const roleArray = localStorage.getItem("roleArray");
        if (!roleArray) return false;
        const checkList = roleArray.split(",");
        return pathArr.some(element => checkList.includes(element));
    }, []);

    const hasFeature = useCallback((key) => {
        if (!allowedFeatures) return false;
        const v = allowedFeatures[key];
        return v === true || v === 'true' || v === 1 || v === '1';
    }, [allowedFeatures]);

    useEffect(() => {
        if (profileDataState && profileDataState.currentUserData.isSuccess) {
            let roleArray = profileDataState.currentUserData.result.features.map(i => i.feature);
            localStorage.setItem("roleArray", roleArray.join(','));
            localStorage.setItem("userData", JSON.stringify(profileDataState.currentUserData.result));

            if (profileDataState.currentUserData.result.companylogo) {
                try {
                    const binaryString = atob(profileDataState.currentUserData.result.companylogo);
                    const byteArray = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        byteArray[i] = binaryString.charCodeAt(i);
                    }
                    const blob = new Blob([byteArray], { type: "image/*" });
                    const url = URL.createObjectURL(blob);
                    setCompanyLogo(url);
                } catch (e) {
                    console.error("Error processing company logo:", e);
                    setCompanyLogo(null);
                }
            } else {
                setCompanyLogo(null);
            }
        }
    }, [profileDataState]);

    const getallActiveDevices = useCallback(() => {
        const deviceService = new DeviceService();
        deviceService.getAllActiveDevices()
            .then(response => {
                if (response.isSuccess === true) {
                    setDeviceList(response.result);
                    dispatch(setDeviceConnectionMessage(""));
                }
            })
            .catch(error => console.error("Error fetching active devices:", error));
    }, [dispatch]);

    const getUserDeviceNewSubscription = useCallback(async () => {
        try {
            const url = `${process.env.REACT_APP_NODE_URL}/api/devices/get-total-device-newsubscription`;
            const config = { headers: { token: localStorage.getItem("isToken") } };
            const response = await axios.get(url, config);
            if (response.data && response.data.result && response.data.result.length > 0) {
                const [item] = response.data.result;
                setActivePlan(item.plan_name);
                setAllowMyAppsShow(item.is_api_access === 1 || item.is_api_access === '1' || item.is_api_access === 'yes' ? 'yes' : 'no');
            }
        } catch (error) {
            console.error("Error fetching user subscription:", error);
        }
    }, []);

    useEffect(() => {
        getallActiveDevices();
        getUserDeviceNewSubscription();
    }, [getallActiveDevices, getUserDeviceNewSubscription]);

    useEffect(() => {
        if (checkDeviceDataState === "Connected Device") {
            getallActiveDevices();
        }
    }, [checkDeviceDataState, getallActiveDevices]);


    const menuItems = [
        { text: "Dashboard", icon: <DashboardIcon />, path: "/home", grant: ["dashboard"] },
        { text: "My Devices", icon: <DevicesIcon />, path: "/mydevices", grant: ["mydevices"] },
        { text: "Send Messages", icon: <SendIcon />, path: "/singlesend", grant: ["singlesend"] },
        { text: "Group Send", icon: <GroupAddIcon />, path: "/groupsendmsg", grant: ["groupsendmsg"] },
        {
            text: "My Apps",
            icon: <AppsIcon />,
            path: "/myapps",
            grant: ["myapps"],
            condition: deviceList.length > 0 && allowMyAppsShow === "yes" && hasFeature('apiAccess'),
        },
        {
            text: "Chatbot",
            icon: <Chat />,
            path: "/chatbot",
            grant: ["chatbot"],
            condition: ["Pro Plan","Enterprise Plan"].includes(activePlan) && hasFeature('autoReply'),
        },
        { text: "Contacts Book", icon: <ContactsOutlinedIcon />, path: "/contactbook", grant: ["contactbook"], condition: hasFeature('contactLimit') },
        { text: "My Templates", icon: <ArticleOutlinedIcon />, path: "/mytemplate", grant: ["templates"] },
        { text: "Send Bulk Messages", icon: <DescriptionOutlinedIcon />, path: "/bulkmessage", grant: ["bulkmessage"] },
        {
            text: "Scheduled Message",
            icon: <AccessTimeIcon />,
            path: "/scheduledmessage",
            grant: ["scheduled"],
            condition: ["Pro Plan", "Enterprise Plan"].includes(activePlan) && hasFeature('scheduleMessages'),
        },
        { text: "Message Log", icon: <HistoryIcon />, path: "/messagelogs", grant: ["log"] },
        { text: "Extract Numbers", icon: <NumbersIcon />, path: "/extractNumbers", grant: ["extractNumbers"] },
        { text: "Black List Numbers", icon: <AppBlockingIcon />, path: "/blacklist", grant: ["blacklist"] },
    ];


    const administrationItems = [
        { text: "Dashboard", icon: <DashboardIcon />, path: "/admindashboard", grant: ["admindashboard"] },
        { text: "Roles", icon: <SupervisedUserCircleIcon />, path: "/roles", grant: ["roles"] },
        { text: "Users", icon: <PeopleIcon />, path: "/users", grant: ["users"] },
        { text: "User Report", icon: <PeopleIcon />, path: "/userreport", grant: ["users"] },
        { text: "Failed Transaction", icon: <SummarizeIcon />, path: "/failedtransactionreport", grant: ["users"] },
        { text: "Subscription Offer", icon: <SummarizeIcon />, path: "/subscriptionoffer", grant: ["users"] },
        { text: "Configuration", icon: <SettingsApplicationsIcon />, path: "/configuration", grant: ["singlesend"] },
    ];

    const bottomMenuItems = [
        { text: "Subscription", icon: <SubscriptionsIcon />, path: "/subscription", grant: ["profilesetting"] },
        { text: "Activity Log", icon: <HistoryIcon />, path: "/activitylog", grant: ["activitylog"] },
        {
            text: "Help & Support",
            icon: <HelpOutlineIcon />,
            path: "/help_support",
            grant: ["profilesetting"],
            badge: true
        },
        { text: "Profile Settings", icon: <SettingsIcon />, path: "/profilesetting", grant: ["profilesetting"] },
    ];

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedAccordion(isExpanded ? panel : false);
    };

    const renderNavListItem = (item, index) => {
        if (item.condition === false) return null;
        if (!checkTabGrant(item.grant)) return null;

        // Simplified click handler. It no longer needs to open the drawer.
        const clickHandler = () => {
            if (onListItemClick) {
                onListItemClick();
            }
        }

        return (
            <ListItem key={item.text + index} disablePadding sx={{ display: "block" }}>
                <ListItemButton
                    component={NavLink}
                    to={item.path}
                    onClick={clickHandler}
                    sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        py: 1.2,
                        mb: 0.5,
                        borderRadius: open ? '8px' : '50%',
                        margin: open ? theme.spacing(0.5, 1) : theme.spacing(0.5, 'auto'),
                        width: open ? `calc(100% - ${theme.spacing(2)})` : theme.spacing(6),
                        height: !open ? theme.spacing(6) : 'auto',
                        color: theme.palette.grey[400],
                        transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
                        position: 'relative',
                        "&:hover": {
                            backgroundColor: alpha(theme.palette.common.white, 0.1),
                            color: theme.palette.common.white,
                            '& .MuiListItemIcon-root': { color: theme.palette.common.white },
                        },
                        "&.active": {
                            backgroundColor: alpha(theme.palette.primary.main, 0.25),
                            color: theme.palette.common.white,
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: '15%',
                                bottom: '15%',
                                width: '4px',
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: '0 4px 4px 0',
                                display: open ? 'block' : 'none',
                            },
                            '& .MuiListItemIcon-root': { color: theme.palette.primary.light },
                        },
                        ...(!open && {
                            '&.active': {
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                '& .MuiListItemIcon-root': { color: theme.palette.primary.contrastText },
                            }
                        })
                    }}
                >
                    <Tooltip title={!open ? item.text : ""} placement="right" arrow>
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: open ? 2 : "auto",
                                justifyContent: "center",
                                color: "inherit",
                                transition: 'color 0.2s ease-in-out',
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                    </Tooltip>
                    {open && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <ListItemText
                                primary={item.text}
                                sx={{
                                    color: "inherit",
                                    '& .MuiTypography-root': { fontWeight: 500 }
                                }}
                            />
                            {item.badge && helpNotifCount > 0 && (
                                <Badge
                                    badgeContent={helpNotifCount}
                                    sx={{
                                        '@keyframes pulse': {
                                            '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)' },
                                            '70%': { boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)' },
                                            '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
                                        },
                                        '& .MuiBadge-badge': {
                                            fontSize: '0.70rem',
                                            height: 20,
                                            minWidth: 20,
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                            animation: 'pulse 2s infinite',
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    )}
                </ListItemButton>
            </ListItem>
        );
    };


    return (
        <StyledDrawer variant="permanent" open={open}>
            {/* The DrawerHeader is simplified to only show the logo. */}
            <DrawerHeader open={open}>
                <img
                    src={open ? (companylogo || "/messagerobohome.svg") : (companylogo || "/messagerobologo.svg")}
                    alt="Company Logo"
                    style={{
                        height: 40,
                        width: open ? 150 : 40,
                        // objectFit: 'contain',
                        transition: 'width 0.3s ease-in-out',
                    }}
                />
            </DrawerHeader>

            <Divider sx={{ my: 1, mx: open ? 2 : 1, bgcolor: alpha(theme.palette.common.white, 0.12) }} />

            <List sx={{ paddingTop: 0, px: open ? 0 : 0.5 }}>
                {menuItems.map(renderNavListItem)}
            </List>

            {checkTabGrant(["administration"]) && (
                <>
                    <Divider sx={{ my: 1, mx: open ? 2 : 1, bgcolor: alpha(theme.palette.common.white, 0.12) }} />
                    <Accordion
                        expanded={open && expandedAccordion === 'adminPanel'}
                        onChange={handleAccordionChange('adminPanel')}
                        disableGutters
                        elevation={0}
                        square
                        sx={{
                            backgroundColor: 'transparent',
                            color: theme.palette.grey[300],
                            '&:before': { display: 'none' },
                            ...(!open && {
                                width: theme.spacing(6),
                                margin: `${theme.spacing(0.5)} auto`,
                                borderRadius: '50%',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.common.white, 0.1)
                                }
                            })
                        }}
                    >
                        <AccordionSummary
                            expandIcon={open ? <ExpandMoreIcon sx={{ color: theme.palette.grey[400] }} /> : null}
                            aria-controls="admin-panel-content"
                            id="admin-panel-header"
                            // The onClick that opened the drawer is removed from here.
                            sx={{
                                minHeight: 48,
                                justifyContent: open ? "initial" : "center",
                                px: 2.5,
                                py: 1.2,
                                mb: 0.5,
                                borderRadius: open ? '8px' : '50%',
                                margin: open ? theme.spacing(0.5, 1) : 'auto',
                                width: open ? `calc(100% - ${theme.spacing(2)})` : theme.spacing(6),
                                height: !open ? theme.spacing(6) : 'auto',
                                '& .MuiAccordionSummary-content': {
                                    margin: open ? '12px 0' : '0',
                                    alignItems: 'center',
                                    flexGrow: open ? 1 : 0,
                                },
                                ...(open && {
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.common.white, 0.08),
                                        color: theme.palette.common.white,
                                        '& .MuiListItemIcon-root': { color: theme.palette.common.white, },
                                        '& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root': { color: theme.palette.common.white, }
                                    },
                                })
                            }}
                        >
                            <Tooltip title={!open ? "Administration" : ""} placement="right" arrow>
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 2 : "auto",
                                        justifyContent: "center",
                                        color: open ? theme.palette.grey[400] : theme.palette.grey[300],
                                    }}
                                >
                                    <SettingsIcon />
                                </ListItemIcon>
                            </Tooltip>
                            {open && <Typography sx={{ fontWeight: 500, color: 'inherit' }}>Administration</Typography>}
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0, pl: open ? 1 : 0 }}>
                            <List disablePadding>
                                {administrationItems.map(renderNavListItem)}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                </>
            )}

            <Divider sx={{ my: 1, mx: open ? 2 : 1, bgcolor: alpha(theme.palette.common.white, 0.12) }} />
            <List sx={{ px: open ? 0 : 0.5 }}>
                {bottomMenuItems.map(renderNavListItem)}
            </List>
        </StyledDrawer>
    );
};

export default Sidebar;